import {
  Box,
  Divider,
  Grid,
  createStyles,
  makeStyles,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core'
import React, { useState } from 'react'
import {
  DateField,
  EditButton,
  FunctionField,
  ReferenceField,
  sanitizeFetchType,
  Show,
  ShowProps,
  Tab,
  TabbedShowLayout,
  TextField,
  TitleProps,
  useMutation,
  useNotify,
  useQuery,
  useRefresh,
  useShowContext,
  useTranslate,
} from 'react-admin'
import MenuIcon from '@mui/icons-material/Menu'
import { Authority } from '../../../../core/auth/Authority'
import { ResourceName } from '../../../../core/ResourceName'
import { useHasAuthority } from '../../../hooks/useHasAuthority'
import { IksorisParkingTicketCashMachineDto } from '../../../../core/dto/device/ticket-machines/iksoris-parking-ticket-cash-machine/iksoris-parking-ticket-cash-machine.dto'
import { DeviceCategoryExtendedDto } from '../../../../core/dto/device-category/device-category-extended.dto'
import { CameraDto } from '../../../../core/dto/device/camera/cameras/camera.dto'
import { DeviceStatusesTab } from '../../common/modules/ModuleStatus'
import { DeviceTypes } from '../../../../core/enum/DeviceTypes'
import Button from '../../../common/customized-mui-components/Button'
import { ConfirmDialog } from '../../../common/ConfirmDialog'
import {
  GetMoneyStateDialog,
  PrintTransactionConfirmationDialog,
  ServiceCashFloatDialog,
  ServiceCashPaymentDialog,
  SetAutomatAPICredentialsDialog,
  SetTicketMachineAPIKeyDialog,
} from './IksorisParkingTicketCashMachineDialogs'
import { BlankActions } from '../../../common/BlankActions'

const useStyles = makeStyles(() =>
  createStyles({
    machineActions: {
      right: '25px',
      position: 'absolute',
      whiteSpace: 'nowrap',
    },
  }),
)

interface IksorisParkingTicketCashMachineTitleProps extends TitleProps {
  record?: IksorisParkingTicketCashMachineDto
}

const ShowTitle = (data: IksorisParkingTicketCashMachineTitleProps) => {
  const { record: terminal } = data
  const translate = useTranslate()
  return (
    <span>
      {translate('resources.iksoris-parking-ticket-cash-machines.titles.show')}:{' '}
      {terminal?.id}. {terminal?.name}
    </span>
  )
}

const GeneralActions = () => {
  const { record: ticketMachine } =
    useShowContext<IksorisParkingTicketCashMachineDto>()
  const hasAuthority = useHasAuthority()

  return (
    <>
      <Divider />
      <Box p={1}>
        <Grid container direction="row" justify="flex-end" alignItems="center">
          <Grid item>
            <EditButton
              basePath={`/${ResourceName.IKSORIS_PARKING_TICKET_CASH_MACHINES}`}
              record={ticketMachine}
              variant="contained"
              style={{ margin: '2px' }}
              disabled={
                !hasAuthority(
                  Authority.EDIT_IKSORIS_PARKING_TICKET_CASH_MACHINES,
                )
              }
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export const IksorisParkingTicketCashMachineTabs = ({ ...props }) => {
  const { simpleView, createdAt, ...rest } = props
  const { record: ticketMachine } = rest
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()
  const [mutate] = useMutation()
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [automatAPICredentialsModalOpen, setAutomatAPICredentialsModalOpen] =
    useState(false)
  const [apiKeyModalOpen, setAPIKeyModalOpen] = useState(false)
  const [removeAPIKeyModalOpen, setRemoveAPIKeyModalOpen] = useState(false)
  const [
    confirmServiceCashEmptyingModalOpen,
    setConfirmServiceCashEmptyingModalOpen,
  ] = useState(false)
  const [getMoneyStateModalOpen, setGetMoneyStateModalOpen] = useState(false)
  const [serviceCashPaymentModalOpen, setServiceCashPaymentModalOpen] =
    useState(false)
  const [serviceCashFloatModalOpen, setServiceCashFloatModalOpen] =
    useState(false)
  const [
    printTransactionConfirmationModalOpen,
    setPrintTransactionConfirmationModalOpen,
  ] = useState(false)
  const [
    removeAutomatAPICredentialsDialogOpen,
    setRemoveAutomatAPICredentialsDialogOpen,
  ] = useState(false)
  const refresh = useRefresh()
  const notify = useNotify()

  const { data: serverDate } = useQuery(
    {
      type: sanitizeFetchType('getServerDateTime'),
      resource: ResourceName.CURRENT_USER,
      payload: {},
    },
    {
      enabled: !simpleView && !createdAt,
    },
  )

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleRequest = async (id: number, fetchType: string) => {
    await mutate(
      {
        type: sanitizeFetchType(fetchType),
        resource: ResourceName.IKSORIS_PARKING_TICKET_CASH_MACHINES,
        payload: { ticketMachineId: id },
      },
      {
        returnPromise: true,
      },
    )
  }

  const handleSetAutomatAPICredentialsDialog = () => {
    setAutomatAPICredentialsModalOpen(true)
  }

  const handleRemoveAutomatAPICredentials = async () => {
    setRemoveAutomatAPICredentialsDialogOpen(false)
    await mutate(
      {
        type: sanitizeFetchType('removeAutomatAPICredentials'),
        resource: ResourceName.IKSORIS_PARKING_TICKET_CASH_MACHINES,
        payload: {
          ticketMachineId: ticketMachine?.id,
        },
      },
      {
        returnPromise: true,
        onFailure: (err) => notify(err?.message, 'error'),
      },
    )
    refresh()
  }

  const handleRemoveAutomatAPICredentialsDialog = () => {
    setRemoveAutomatAPICredentialsDialogOpen(true)
  }

  const handleSetAPIKeyDialog = () => {
    setAPIKeyModalOpen(true)
  }

  const handleRemoveAPIKey = async () => {
    setRemoveAPIKeyModalOpen(false)
    await mutate(
      {
        type: sanitizeFetchType('removeAPIKey'),
        resource: ResourceName.IKSORIS_PARKING_TICKET_CASH_MACHINES,
        payload: {
          ticketMachineId: ticketMachine?.id,
        },
      },
      {
        returnPromise: true,
        onFailure: (err) => notify(err?.message, 'error'),
      },
    )
    refresh()
  }

  const handleRemoveAPIKeyDialog = () => {
    setRemoveAPIKeyModalOpen(true)
  }

  const handleServiceCashEmptying = async () => {
    setConfirmServiceCashEmptyingModalOpen(false)
    await mutate(
      {
        type: sanitizeFetchType('serviceCashEmptying'),
        resource: ResourceName.IKSORIS_PARKING_TICKET_CASH_MACHINES,
        payload: {
          ticketMachineid: ticketMachine?.id,
        },
      },
      {
        returnPromise: true,
        onFailure: (err) => notify(err?.message, 'error'),
      },
    )
  }

  const handleConfirmServiceCashEmptyingDialog = () => {
    setConfirmServiceCashEmptyingModalOpen(true)
  }

  const handleServiceCashPaymentDialog = () => {
    setServiceCashPaymentModalOpen(true)
  }

  const handleServiceCashFloatDialog = () => {
    setServiceCashFloatModalOpen(true)
  }

  const handlePrintTransactionConfirmationDialog = () => {
    setPrintTransactionConfirmationModalOpen(true)
  }

  const handleGetMoneyStateDialog = () => {
    setGetMoneyStateModalOpen(true)
  }

  return (
    <TabbedShowLayout {...rest} syncWithLocation={!simpleView}>
      <Tab label="resources.iksoris-parking-ticket-cash-machines.tabs.general">
        <div className={classes.machineActions}>
          {hasAuthority(
            Authority.IKSORIS_PARKING_TICKET_CASH_MACHINES_ACTIONS_LIST,
          ) && (
            <Button
              onClick={handleClick}
              label="const.selectAction"
              variant="contained"
            >
              <MenuIcon />
            </Button>
          )}
          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
            onClick={handleClose}
          >
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASH_MACHINES_LOCK_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={() => handleRequest(props?.record?.id, 'lock')}
                button
              >
                <Typography>
                  {translate(
                    'resources.selfpos-access-ticket-cashless-machines.actions.lock',
                  )}
                </Typography>
              </MenuItem>
            )}
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASH_MACHINES_UNLOCK_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={() => handleRequest(props?.record?.id, 'unlock')}
                button
              >
                <Typography>
                  {translate(
                    'resources.selfpos-access-ticket-cashless-machines.actions.unlock',
                  )}
                </Typography>
              </MenuItem>
            )}
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASH_MACHINES_RESTART_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={() => handleRequest(props?.record?.id, 'restart')}
                button
              >
                <Typography>
                  {translate(
                    'resources.selfpos-access-ticket-cashless-machines.actions.restart',
                  )}
                </Typography>
              </MenuItem>
            )}
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASH_MACHINES_REBOOT_DEVICE_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={() => handleRequest(props?.record?.id, 'rebootDevice')}
                button
              >
                <Typography>
                  {translate(
                    'resources.selfpos-access-ticket-cashless-machines.actions.reboot-device',
                  )}
                </Typography>
              </MenuItem>
            )}
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASH_MACHINES_SHUTDOWN_DEVICE_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={() =>
                  handleRequest(props?.record?.id, 'shutDownDevice')
                }
                button
              >
                <Typography>
                  {translate(
                    'resources.selfpos-access-ticket-cashless-machines.actions.shutdown-device',
                  )}
                </Typography>
              </MenuItem>
            )}
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASH_MACHINES_MANAGE_CREDENTIALS_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={handleSetAutomatAPICredentialsDialog}
              >
                <Typography>
                  {translate(
                    'resources.ticket-machines.actions.setAutomatAPICredentials',
                  )}
                </Typography>
              </MenuItem>
            )}
            <SetAutomatAPICredentialsDialog
              open={automatAPICredentialsModalOpen}
              close={() => setAutomatAPICredentialsModalOpen(false)}
              resource={ResourceName.IKSORIS_PARKING_TICKET_CASH_MACHINES}
            />
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASH_MACHINES_MANAGE_CREDENTIALS_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={handleRemoveAutomatAPICredentialsDialog}
              >
                <Typography>
                  {translate(
                    'resources.ticket-machines.actions.removeAutomatAPICredentials',
                  )}
                </Typography>
              </MenuItem>
            )}
            <ConfirmDialog
              open={removeAutomatAPICredentialsDialogOpen}
              confirm={handleRemoveAutomatAPICredentials}
              source="setAutomatAPICredentials"
              cancel={() => setRemoveAutomatAPICredentialsDialogOpen(false)}
              action="remove"
              resource={ResourceName.IKSORIS_PARKING_TICKET_CASH_MACHINES}
            />
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASH_MACHINES_MANAGE_CREDENTIALS_BUTTON,
            ) && (
              <MenuItem {...props} onClick={handleSetAPIKeyDialog}>
                <Typography>
                  {translate('resources.ticket-machines.actions.setAPIKey')}
                </Typography>
              </MenuItem>
            )}
            <SetTicketMachineAPIKeyDialog
              open={apiKeyModalOpen}
              close={() => setAPIKeyModalOpen(false)}
              resource={ResourceName.IKSORIS_PARKING_TICKET_CASH_MACHINES}
            />
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASH_MACHINES_MANAGE_CREDENTIALS_BUTTON,
            ) && (
              <MenuItem {...props} onClick={handleRemoveAPIKeyDialog}>
                <Typography>
                  {translate('resources.ticket-machines.actions.removeAPIKey')}
                </Typography>
              </MenuItem>
            )}
            <ConfirmDialog
              open={removeAPIKeyModalOpen}
              confirm={handleRemoveAPIKey}
              source="apiKey"
              cancel={() => setRemoveAPIKeyModalOpen(false)}
              action="remove"
              resource={ResourceName.IKSORIS_PARKING_TICKET_CASH_MACHINES}
            />
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASH_MACHINES_SERVICE_CASH_EMPTYING_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={handleConfirmServiceCashEmptyingDialog}
              >
                <Typography>
                  {translate(
                    'resources.ticket-machines.actions.serviceCashEmptying',
                  )}
                </Typography>
              </MenuItem>
            )}
            <ConfirmDialog
              open={confirmServiceCashEmptyingModalOpen}
              confirm={handleServiceCashEmptying}
              source="serviceCashEmptying"
              cancel={() => setConfirmServiceCashEmptyingModalOpen(false)}
              action="set"
              resource={ResourceName.IKSORIS_PARKING_TICKET_CASH_MACHINES}
            />
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASH_MACHINES_SERVICE_CASH_PAYMENT_BUTTON,
            ) && (
              <MenuItem {...props} onClick={handleServiceCashPaymentDialog}>
                <Typography>
                  {translate(
                    'resources.ticket-machines.actions.serviceCashPayment',
                  )}
                </Typography>
              </MenuItem>
            )}
            <ServiceCashPaymentDialog
              open={serviceCashPaymentModalOpen}
              close={() => setServiceCashPaymentModalOpen(false)}
              resource={ResourceName.IKSORIS_PARKING_TICKET_CASH_MACHINES}
            />
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASH_MACHINES_SERVICE_CASH_FLOAT_BUTTON,
            ) && (
              <MenuItem {...props} onClick={handleServiceCashFloatDialog}>
                <Typography>
                  {translate(
                    'resources.ticket-machines.actions.serviceCashFloat',
                  )}
                </Typography>
              </MenuItem>
            )}
            <ServiceCashFloatDialog
              open={serviceCashFloatModalOpen}
              close={() => setServiceCashFloatModalOpen(false)}
              resource={ResourceName.IKSORIS_PARKING_TICKET_CASH_MACHINES}
            />
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASH_MACHINES_GENERATE_TRANSACTIONS_INFO_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={handlePrintTransactionConfirmationDialog}
              >
                <Typography>
                  {translate(
                    'resources.ticket-machines.actions.printTransactionConfirmation',
                  )}
                </Typography>
              </MenuItem>
            )}
            <PrintTransactionConfirmationDialog
              open={printTransactionConfirmationModalOpen}
              close={() => setPrintTransactionConfirmationModalOpen(false)}
              resource={ResourceName.AUTOMAT_API_TRANSACTIONS}
            />
            {hasAuthority(
              Authority.IKSORIS_PARKING_TICKET_CASH_MACHINES_GET_MONEY_STATE_BUTTON,
            ) && (
              <MenuItem {...props} onClick={handleGetMoneyStateDialog}>
                <Typography>
                  {translate('resources.ticket-machines.actions.getMoneyState')}
                </Typography>
              </MenuItem>
            )}
            <GetMoneyStateDialog
              open={getMoneyStateModalOpen}
              close={() => setGetMoneyStateModalOpen(false)}
              resource={ResourceName.IKSORIS_PARKING_TICKET_CASH_MACHINES}
            />
          </Menu>
        </div>
        <TextField source="id" label="ID" />
        <TextField source="name" />
        <FunctionField<IksorisParkingTicketCashMachineDto>
          source="isWatched"
          render={(record?: IksorisParkingTicketCashMachineDto) => (
            <div>
              {record?.isWatched
                ? translate('const.yes')
                : translate('const.no')}
            </div>
          )}
        />
        <DateField
          source="lastRefresh"
          showTime
          options={{
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }}
        />
        {ticketMachine?.categoryId &&
          hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
            <ReferenceField
              source="categoryId"
              reference={ResourceName.DEVICE_CATEGORIES}
              link="show"
            >
              <FunctionField<DeviceCategoryExtendedDto>
                source="hierarchyString"
                render={(record?: DeviceCategoryExtendedDto) =>
                  `${record?.id}. ${record?.hierarchyString}`
                }
              />
            </ReferenceField>
          )}
        {ticketMachine?.cameraId && hasAuthority(Authority.VIEW_CAMERAS) && (
          <ReferenceField
            source="cameraId"
            reference={ResourceName.CAMERAS}
            link="show"
          >
            <FunctionField<CameraDto>
              source="name"
              render={(camera?: CameraDto) =>
                `${camera?.id}. ${camera?.description}`
              }
            />
          </ReferenceField>
        )}
        <TextField source="address" />
        <TextField source="automatAPIAddress" />
        {(!simpleView && <GeneralActions />) || <BlankActions />}
      </Tab>
      <Tab
        label="resources.iksoris-parking-ticket-cash-machines.tabs.statuses"
        path="statuses"
      >
        {ticketMachine?.jsonStatus && (
          <DeviceStatusesTab
            jsonStatus={ticketMachine.jsonStatus}
            deviceType={DeviceTypes.IKSORIS_PARKING_TICKET_CASH_MACHINE}
            referenceDateTime={createdAt || serverDate?.serverDateTime}
          />
        )}
      </Tab>
    </TabbedShowLayout>
  )
}

export const IksorisParkingTicketCashMachineShow = (props: ShowProps) => (
  <Show {...props} actions={false} title={<ShowTitle />}>
    <IksorisParkingTicketCashMachineTabs />
  </Show>
)
